/**
 * Fetches and renders custom datasets (charts) for a given page and location.
 * Renders each as CustomDataChart (time series, bar, or table) in the same local content area as custom sections.
 */

import React, { useEffect, useState } from 'react';
import { fetchCustomDatasets, type CustomDatasetPageId } from '../../services/customDatasetsService';
import { CustomDataChart } from './CustomDataChart';

interface CustomDatasetsForPageProps {
  pageId: CustomDatasetPageId;
  locationName: string;
}

export const CustomDatasetsForPage: React.FC<CustomDatasetsForPageProps> = ({ pageId, locationName }) => {
  const [datasets, setDatasets] = useState<Awaited<ReturnType<typeof fetchCustomDatasets>>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!locationName) {
      setDatasets([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchCustomDatasets(locationName, pageId).then((data) => {
      if (!cancelled) {
        setDatasets(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locationName, pageId]);

  if (loading || datasets.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {datasets.map((dataset) => (
        <CustomDataChart key={dataset.id} dataset={dataset} />
      ))}
    </div>
  );
};

export default CustomDatasetsForPage;
